import {
    DownloadIcon,
    UpdateIcon
} from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/use-toast';

export default function ExportJudgesStatsDialog({ dropdown }) {

    const [selectedYear, setSelectedYear] = useState(null); // Start as null
    const [judgesData, setJudgesData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // New state for loader

    const fetchJudgeStats = async () => {
        if (!selectedYear) return; // Ensure a year is selected before making the call

        setIsLoading(true); // Start loader

        try {
            const response = await api.get(`https://skhrf-api.cmdigital.se/v1/judges/judge-stats?year=${selectedYear}`);
            setJudgesData(response.data);
            setCsvData(response.data.map(judge => ({
                year: judge.year,
                judgeId: judge.judgeId,
                firstName: judge.firstName,
                lastName: judge.lastName,
                competitionsJudged: judge.competitionsJudged,
                competitionClassesJudged: judge.competitionClassesJudged,
                startsJudged: judge.startsJudged
            })));
        } catch (error) {
            toast({ title: "Error fetching judge stats", description: error.message });
        } finally {
            setIsLoading(false); // Stop loader
        }
    };

    useEffect(() => {
        if (selectedYear) {
            fetchJudgeStats();
        }
    }, [selectedYear]);

    const generateYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 2022; year <= currentYear; year++) {
            years.push(year);
        }
        return years;
    };

    const headers = [
        { label: "År", key: "year" },
        { label: "Domar ID", key: "judgeId" },
        { label: "Förnamn", key: "firstName" },
        { label: "Efternamn", key: "lastName" },
        { label: "Dömda tävlingar", key: "competitionsJudged" },
        { label: "Dömda klasser", key: "competitionClassesJudged" },
        { label: "Dömda starter", key: "startsJudged" },
    ];

    return (
        <Dialog>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className='flex items-center w-full'>
                        <DownloadIcon className='mr-3'/>
                        Exportera all domarstatistik
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full' asChild>
                    <Button>
                        <DownloadIcon className='mr-3'/>
                        Exportera all domarstatistik
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="flex flex-col gap-6" onOpenAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader className='text-left'>
                    <DialogTitle>Exportera statistik för domare</DialogTitle>
                    <DialogDescription>
                        Välj år nedan och ladda ner statistik för domare.
                    </DialogDescription>
                </DialogHeader>
                <div className='flex flex-col gap-2'>
                    <Label>År</Label>
                    <Select value={selectedYear?.toString() || ''} onValueChange={(year) => setSelectedYear(parseInt(year))}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Välj år..." />
                        </SelectTrigger>
                        <SelectContent>
                            {generateYears().map(year => (
                                <SelectItem key={year} value={year.toString()}>
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {selectedYear && (
                    judgesData.length > 0 ? (
                        <p className='text-sm'>
                            {judgesData.length} domare har tillgänglig data för {selectedYear}.
                        </p>
                    ) : (
                        <p className='text-sm'>Inga domare har tillgänglig data för {selectedYear}.</p>
                    )
                )}


                <div className='grid grid-cols-2 gap-3'>
                    <DialogClose asChild>
                        <Button variant='secondary'>Stäng</Button>
                    </DialogClose>
                    <Button disabled={judgesData.length <= 0 || isLoading}>
                        {isLoading ? (
                            <UpdateIcon className="loader-spinner" />
                        ) : (
                            <CSVLink
                                data={csvData}
                                headers={headers}
                                filename={`judges_stats_${selectedYear}.csv`}
                                className='flex items-center justify-center w-full'>
                                <DownloadIcon className="mr-3" />
                                Ladda ner
                            </CSVLink>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
